/*
Login page — 2-step passwordless flow.

Step 1: enter email, submit "Send code". The server emails a 6-digit
code valid for 1 hour. The response is always 200 (anti-enumeration);
the UI optimistically advances to step 2 and tells the user to check
their inbox.

Step 2: enter the 6-digit code, submit "Sign in". On success the
auth context opens the session and the App router redirects to /cards.
A "Use a different email" link sends the user back to step 1; a
"Resend code" button re-triggers /request-code which invalidates any
prior unconsumed code on the server.
*/

import { useState, type FormEvent, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "../lib/api";
import { useAuth, consumeSessionExpiredFlag } from "../lib/auth";

type Step = "email" | "code";	// Which sub-form is currently rendered

export function LoginPage(): JSX.Element
{
	const { requestCode, verifyCode } = useAuth();	// Auth context
	const { t }		= useTranslation("auth");
	const { t: tc }	= useTranslation("common");

	// Stamped in sessionStorage by AuthProvider when an authenticated
	// request returned 401 (token expired / revoked). Consumed once on
	// the first render of LoginPage; subsequent renders / page navs
	// see the flag cleared.
	const [fSessionExpired] = useState<boolean>(() => consumeSessionExpiredFlag());

	const [fStep,   fSetStep]	= useState<Step>("email");
	const [fEmail,  fSetEmail]	= useState<string>("");
	const [fCode,   fSetCode]	= useState<string>("");
	const [fError,  fSetError]	= useState<string | null>(null);
	const [fInfo,   fSetInfo]	= useState<string | null>(null);
	const [fBusy,   fSetBusy]	= useState<boolean>(false);

	const fHandleEmailSubmit = async (inEvent: FormEvent<HTMLFormElement>) =>
	{
		inEvent.preventDefault();
		fSetError(null);
		fSetInfo(null);
		fSetBusy(true);

		try
		{
			await requestCode(fEmail.trim());
			fSetStep("code");
			fSetInfo(t("code_sent_info"));
		}
		catch (vErr)
		{
			fSetError(translate(t, vErr));
		}
		finally
		{
			fSetBusy(false);
		}
	};

	const fHandleCodeSubmit = async (inEvent: FormEvent<HTMLFormElement>) =>
	{
		inEvent.preventDefault();
		fSetError(null);
		fSetInfo(null);
		fSetBusy(true);

		try
		{
			await verifyCode(fEmail.trim(), fCode.trim());
			// On success the parent App re-renders and routes to /cards;
			// no explicit navigation needed here.
		}
		catch (vErr)
		{
			fSetError(translate(t, vErr));
		}
		finally
		{
			fSetBusy(false);
		}
	};

	const fHandleResend = async () =>
	{
		fSetError(null);
		fSetInfo(null);
		fSetBusy(true);

		try
		{
			await requestCode(fEmail.trim());
			fSetInfo(t("code_resent_info"));
			fSetCode("");
		}
		catch (vErr)
		{
			fSetError(translate(t, vErr));
		}
		finally
		{
			fSetBusy(false);
		}
	};

	const fHandleChangeEmail = () =>
	{
		fSetStep("email");
		fSetCode("");
		fSetError(null);
		fSetInfo(null);
	};

	return (
		<div className="login-page">
			{fStep === "email"
				? (
					<form className="login-card" onSubmit={fHandleEmailSubmit}>
						<h1>{t("login_title")}</h1>
						<p className="muted">{t("login_subtitle_passwordless")}</p>

						{fSessionExpired
							? <div className="error">{t("session_expired_banner")}</div>
							: null}

						<label htmlFor="login-email">{t("email_label")}</label>
						<input
							id="login-email"
							type="email"
							autoComplete="email"
							value={fEmail}
							onChange={(e) => fSetEmail(e.target.value)}
							required
							autoFocus
						/>

						{fError !== null ? <div className="error">{fError}</div> : null}

						<div style={{ height: "1rem" }} />

						<button type="submit" className="button" disabled={fBusy} style={{ width: "100%" }}>
							{fBusy ? tc("sending") : t("send_code_submit")}
						</button>
					</form>
				)
				: (
					<form className="login-card" onSubmit={fHandleCodeSubmit}>
						<h1>{t("login_title")}</h1>
						<p className="muted">{t("code_step_subtitle", { email: fEmail })}</p>

						{fInfo !== null
							? <div className="muted" style={{ color: "var(--color-success, #1b5e20)" }}>{fInfo}</div>
							: null}

						<label htmlFor="login-code">{t("code_label")}</label>
						<input
							id="login-code"
							type="text"
							inputMode="numeric"
							autoComplete="one-time-code"
							pattern="\d{6}"
							maxLength={6}
							value={fCode}
							onChange={(e) => fSetCode(e.target.value.replace(/\D/g, ""))}
							required
							autoFocus
						/>

						{fError !== null ? <div className="error">{fError}</div> : null}

						<div style={{ height: "1rem" }} />

						<button type="submit" className="button" disabled={fBusy || fCode.length !== 6} style={{ width: "100%" }}>
							{fBusy ? t("login_progress") : t("login_submit")}
						</button>

						<div style={{ height: "0.75rem" }} />

						<div style={{ display: "flex", justifyContent: "space-between" }}>
							<button
								type="button"
								className="button button--ghost"
								onClick={fHandleChangeEmail}
								disabled={fBusy}
							>
								{t("change_email_link")}
							</button>
							<button
								type="button"
								className="button button--ghost"
								onClick={fHandleResend}
								disabled={fBusy}
							>
								{t("resend_code_link")}
							</button>
						</div>
					</form>
				)}
		</div>
	);
}

/*
Translates a server-side error code via the i18n auth namespace.
*/
function translate(
	inT:	(key: string) => string,
	inErr:	unknown
): string
{
	if (inErr instanceof ApiError)
	{
		switch (inErr.code)
		{
			case "invalid_credentials":	return inT("err_invalid_credentials");
			case "account_disabled":	return inT("err_account_disabled");
			case "missing_field":		return inT("err_missing_field");
			case "rate_limited":		return inT("err_rate_limited");
			default:					return inT("err_default");
		}
	}

	return inT("err_default");
}
